import { PromiseExtended, Table } from 'dexie';
import { AnnouncementGroup, IAnnouncements } from '../types/notification';
import { SyncRecord, SyncChanges, SyncRecordStatus } from '../types/sync-state';
import { BaseModelForPush } from './base-model';
const SCHEMA_VERSION = 2;
const SCHEMA = {
  announcements: 'id, syncStatus, seen, created',
};
export interface AnnouncementsRecords extends IAnnouncements, SyncRecord {}

export class Notifications extends BaseModelForPush {
  announcements!: Table<AnnouncementsRecords>;

  constructor() {
    super('notifications');
    this.version(SCHEMA_VERSION).stores(SCHEMA);
    this.setupTableHooks(this.tables);
  }

  clearData() {
    this.tables.forEach((table) => table.clear());
  }

  // announcements//
  async getAnnouncements(): Promise<IAnnouncements[]> {
    return await this.announcements
      .filter((data) => data.syncStatus !== SyncRecordStatus.Deleted)
      .toArray()
      .then((data) => data.sort((a, b) => Number(a.seen) - Number(b.seen)));
  }
  async getAnnouncementCount(): Promise<number> {
    return this.announcements
      .filter(
        (notification) =>
          notification.seen === false &&
          notification.syncStatus !== SyncRecordStatus.Deleted,
      )
      .count();
  }

  async readAnnouncement(id: number) {
    return await this.announcements.update(id, { seen: true });
  }
  async getAllReadNotifation() {
    return this.announcements.filter(
      (notification) =>
        notification.seen &&
        notification.syncStatus !== SyncRecordStatus.Deleted,
    );
  }
  private async getSyncedRecord(
    model: Table,
  ): Promise<PromiseExtended<SyncRecord[]>> {
    const records = await model
      .where('syncStatus')
      .anyOf([SyncRecordStatus.Updated])
      .toArray();
    return records.map((record) => {
      if (record.seen === true) {
        return record;
      }
    });
  }
  async getAllUnsyncedRecords(): Promise<Record<string, SyncRecord[]>> {
    const records = {
      announcements: await this.getSyncedRecord(this.announcements),
    };
    return records;
  }
  async updateRecordsSyncStatus(
    syncChanges: SyncChanges,
    status: SyncRecordStatus,
  ): Promise<number> {
    let updates = 0;

    const idsToMarkSynced = {
      announcements: syncChanges.announcements?.updated?.map(
        (record: { id: number }) => record.id,
      ),
    };
    for (const [model, ids] of Object.entries(idsToMarkSynced)) {
      updates += await this._updateSyncData(
        // @ts-ignore
        this[model],
        ids,
        status,
      );
    }
    return 1;
  }
  groupNotificationsByDate(
    notifications: IAnnouncements[],
  ): Record<AnnouncementGroup, IAnnouncements[]> {
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const startOfToday = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate(),
    );
    const startOfYesterday = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      yesterday.getDate(),
    );
    const startOfLastSevenDays = new Date(today);
    startOfLastSevenDays.setDate(today.getDate() - 6); // Includes today
    const startOfLastThirtyDays = new Date(today);
    startOfLastThirtyDays.setDate(today.getDate() - 29); // Includes today

    const grouped: Record<AnnouncementGroup, IAnnouncements[]> = {
      today: [],
      yesterday: [],
      lastSevenDays: [],
      thisMonth: [],
    };

    notifications.forEach((notification) => {
      const createdDate = new Date(notification.created);

      if (createdDate >= startOfToday) {
        grouped.today.push(notification);
      } else if (
        createdDate >= startOfYesterday &&
        createdDate < startOfToday
      ) {
        grouped.yesterday.push(notification);
      } else if (createdDate >= startOfLastSevenDays) {
        grouped.lastSevenDays.push(notification);
      } else if (createdDate >= startOfLastThirtyDays) {
        grouped.thisMonth.push(notification);
      }
    });

    return grouped;
  }
}
export const notificationDb = new Notifications();
